import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

import { AccountingActivationService } from '../services/accounting-activation.service';
import { StateService } from '../services/state.service';

/**
 * Accounting entitlement guard.
 *
 * SHAPE NOTE — this is ONE guard where payroll has two.
 * Payroll mounts `/payroll/activate` as a SIBLING of the payroll shell, so it
 * can put `canActivate` on the feature routes and `canMatch` on the wizard.
 * Accounting nests `activate/plan` UNDER `accounting`, so a single guard on the
 * parent route sees every accounting URL and branches on it. That keeps the
 * wiring to one line in app-routing.module.ts instead of a `canActivate` on
 * each of ~35 feature routes, which is where drift would creep in.
 *
 * Four behaviours, by URL:
 *   /accounting/activate/**  → active company is redirected OUT to the hub
 *                              (never drop a paying customer back in the wizard)
 *   /accounting/home         → always allowed; the page itself renders either
 *                              the briefing or the hub
 *   everything else          → requires the company to have been ACTIVATED at
 *                              some point, not to be currently paid up. What an
 *                              expired client may still POST is enforced
 *                              server-side by date, not by locking the module.
 *
 * …and cutting across all of them: a BOOKKEEPING client on THIRD-PARTY software
 * has no accounting module at all, so the whole area resolves to
 * /accounting/engagement, with payment and the wizard left reachable.
 *
 * FAIL-OPEN is deliberate, and matches payroll. The summary is resolved inside
 * a short budget; a timeout or a backend error lets the navigation through and
 * lets the page render its own empty state. Locking a paying customer out of
 * their books because a status endpoint was slow is worse than briefly showing
 * a page to someone who should not see it.
 */

/**
 * The accounting pages a SUSPENDED company may still open: the on-hold screen
 * itself, My Plan, the Airwallex return pages (how they see and settle what is
 * owed) and the wizard, which this guard already lets them into. Everything
 * else goes back to /accounting/home. Shared with AppComponent so the guard and
 * the after-the-fact check cannot drift apart.
 */
export function isOpenWhileSuspended(url: string): boolean {
  return [
    '/accounting/home',
    '/accounting/engagement',
    '/accounting/payment-success',
    '/accounting/payment-cancelled',
    '/accounting/activate'
  ].some((a) => url.startsWith(a));
}

/**
 * The accounting pages a bookkeeping client on THIRD-PARTY software may open:
 * My Plan, the Airwallex return pages and the wizard (renewal). Everything else
 * resolves to /accounting/engagement. Shared with AppComponent, which repeats
 * the check after the fact — this guard sits on the parent route, so it does
 * not re-run between accounting pages, and it fails open on a slow status.
 */
export function isOpenForThirdPartySoftware(url: string): boolean {
  return [
    '/accounting/engagement',
    '/accounting/payment-success',
    '/accounting/payment-cancelled',
    '/accounting/activate'
  ].some((a) => url.startsWith(a));
}

/** Resolve budget. Past this we stop waiting and let the navigation through. */
const RESOLVE_BUDGET_MS = 1500;

/** How long to wait for a company to appear in state on a cold load. */
const COMPANY_WAIT_MS = 4000;
const COMPANY_POLL_MS = 100;

function companyIdOf(state: StateService): string {
  const company = state.selectedCompany || {};
  return String(company['companyID'] || company['id'] || '');
}

/** A cold deep-link can beat company resolution; give it a moment. */
async function waitForCompany(state: StateService): Promise<string> {
  const deadline = Date.now() + COMPANY_WAIT_MS;
  let id = companyIdOf(state);
  while (!id && Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, COMPANY_POLL_MS));
    id = companyIdOf(state);
  }
  return id;
}

/** Resolve within the budget, or give up and return undefined (= fail open). */
function withBudget<T>(p: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([
    p,
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), ms))
  ]);
}

export const accountingActivationGuard: CanActivateFn = async (
  _route,
  state
): Promise<boolean | UrlTree> => {
  const activation = inject(AccountingActivationService);
  const stateService = inject(StateService);
  const router = inject(Router);

  const url = state.url || '';
  const isWizard = url.startsWith('/accounting/activate');
  const isHome = url.startsWith('/accounting/home');

  const companyId = await waitForCompany(stateService);
  // No company selected — nothing to gate on. Let the page handle it.
  if (!companyId) return true;

  const summary = await withBudget(activation.load(companyId), RESOLVE_BUDGET_MS);

  // Budget blown or backend unreachable → fail open.
  if (summary === undefined || summary === null) return true;

  const isActive = summary.activation_status === 'active';

  // Has this company ever been activated?
  //
  // NOT the same question as "is the subscription current". An expired company
  // keeps its books: it may come in, read, export and correct anything inside
  // the period it paid for, and is refused server-side only when it dates a
  // record after its coverage ends. Sending it back to the activation wizard
  // was a renewal wall across a client's own ledger.
  //
  // `suspended` is in here too — an unpaid instalment stops new records, not
  // access to existing ones.
  const hasEntered = summary.can_enter
    ?? ['active', 'suspended', 'expired'].includes(summary.activation_status);

  // ---- the activation wizard --------------------------------------------
  if (isWizard) {
    // A RENEWAL is an explicit request, not a wrong turn.
    //
    // The rule below bounces anyone active or already paid, which is right for
    // someone who wandered in — but it also caught every renewer, including the
    // expired ones the comment claims to allow: `payment_status` is the LATEST
    // INVOICE's status, and a client who completed a term has a paid one. So
    // `renewPlan()` round-tripped straight back to the hub and the Renew button
    // did nothing.
    //
    // `?mode=renew` is the client saying so, and `hasEntered` is the server
    // agreeing they have something to renew. Both are required.
    const wantsRenewal = url.includes('mode=renew');
    if (wantsRenewal && hasEntered) return true;
    // An UPGRADE is the same kind of explicit request: an active DIY client
    // choosing bookkeeping. Bookkeeping clients have nowhere up to go.
    const wantsUpgrade = url.includes('mode=upgrade');
    if (wantsUpgrade && isActive && summary.plan === 'diy') return true;

    // Active, or already paid: send them to the hub. Never drop a paying
    // customer back into the wizard.
    if (isActive || summary.payment_status === 'paid') {
      return router.parseUrl('/accounting/home');
    }
    // An EXPIRED company is allowed into the wizard — that is how it renews.
    return true;
  }

  // ---- bookkeeping kept in someone else's software -----------------------
  //
  // There is no accounting module for these clients. Their books live in Xero
  // or SQL or Bukku; nothing here has a ledger to show, so every feature route
  // opens onto an empty screen that looks broken rather than inapplicable.
  // The engagement page is the whole product for them — what they bought, when
  // we close it, what it costs — so the accounting area resolves to it.
  //
  // Placed BEFORE the `isHome` allowance deliberately: /accounting/home renders
  // the hub, which is a grid of tiles into exactly the screens this is keeping
  // them out of.
  //
  // MONEY AND RENEWAL STAY REACHABLE. The wizard already returned above, and
  // the two Airwallex RETURN URLs are exempt here: a client who has just paid
  // comes back to /accounting/payment-success, and bouncing them off it would
  // replace their receipt with a redirect — for a payment that went through.
  // (Paying itself happens in a modal on the engagement page, so there is no
  // separate payment screen to allow.)
  if (activation.usesThirdPartySoftware()) {
    return isOpenForThirdPartySoftware(url) ? true : router.parseUrl('/accounting/engagement');
  }

  // ---- the briefing / hub page ------------------------------------------
  // Renders the briefing for unactivated companies and the hub for active
  // ones, so it is always reachable.
  if (isHome) return true;

  // ---- suspended: the on-hold screen is the way in ------------------------
  // An unpaid instalment during a live term. /accounting/home renders the
  // "on hold" screen with the payment widget, and the top bar is hidden, but a
  // typed URL or bookmark used to open every feature page anyway. Send those
  // back to the on-hold screen. This guard fails open on a slow status, so
  // AppComponent repeats the same check once the status arrives.
  if (summary.activation_status === 'suspended') {
    return isOpenWhileSuspended(url) ? true : router.parseUrl('/accounting/home');
  }

  // ---- every other accounting feature route -----------------------------
  if (hasEntered) {
    // Post-activation hard gate: the financial year end must be set before
    // the rest of the product opens up, because every report silently defaults
    // to a December year end without it.
    //
    // Points at /accounting/setting, NOT /accounting/profile — the latter has
    // never existed, so this redirect was a 404 waiting for the first company
    // with a null year_end.
    if (summary.setup_steps?.accounting_profile === false) {
      // My Plan is exempt, like `home` above. It is not a ledger screen — no
      // report on it defaults to a December year end, because it shows no
      // reports. A client who has paid and not yet set a year end would
      // otherwise be bounced off the one page that tells them what they bought,
      // and the page itself is where they read that the year end is missing.
      if (url.startsWith('/accounting/engagement')) return true;
      const target = '/accounting/setting';
      // Loop protection — never redirect a URL to itself.
      return url.startsWith(target) ? true : router.parseUrl(target);
    }
    return true;
  }

  const target = activation.stageToUrl(activation.nextStage(summary));
  return url.startsWith(target) ? true : router.parseUrl(target);
};
